import { useServiceToken } from '@ltvco/refresh-lib/v1';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@ltvco/refresh-lib/vendors';
import {
  createComment,
  getSocialSentiment,
  getSocialSentiments,
} from 'api/requests/socialSentiments/socialSentiments';
import { useContext } from 'react';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  CreateCommentParams,
  SocialSentimentsResponse,
} from 'api/requests/socialSentiments/interfaces';

export const useSocialSentiment = (
  year: string,
  make: string,
  model: string,
  trim: string,
  socialSentimentId?: number,
) => {
  const { data: oramTokenData } = useServiceToken('oram');
  const { logError } = useContext(AppConfig);
  const queryClient = useQueryClient();

  const token = oramTokenData?.token ?? '';

  const hasToken = Boolean(token);
  const hasParams =
    Boolean(year) && Boolean(make) && Boolean(model) && Boolean(trim);

  const createCommentMutation = useMutation({
    mutationFn: (params: CreateCommentParams) => {
      // Get fresh token on each mutation call to avoid stale token
      const currentToken = oramTokenData?.token ?? '';
      return createComment(params, currentToken);
    },
    onSuccess: (newComment, params) => {
      queryClient.setQueryData(
        ['social-sentiments', year, make, model, trim],
        (previous: SocialSentimentsResponse | undefined) => {
          if (!previous) {
            return previous;
          }

          const updatedItems = previous.items.map((item) => {
            if (item.id !== params.social_sentiment_id) {
              return item;
            }

            const updated = { ...item };
            if (typeof updated.comments_count === 'number') {
              updated.comments_count += 1;
            } else if (typeof updated.comment_count === 'number') {
              updated.comment_count += 1;
            } else if (Array.isArray(updated.comments)) {
              updated.comments = [...updated.comments, newComment];
            } else {
              updated.comments_count = 1;
            }

            return updated;
          });

          return {
            ...previous,
            items: updatedItems,
          };
        },
      );

      queryClient.setQueryData(
        ['social-sentiments', params.social_sentiment_id],
        (previous: any) => {
          if (!previous) {
            return previous;
          }

          const existingComments = Array.isArray(previous.comments)
            ? previous.comments
            : [];

          const updated: any = {
            ...previous,
            comments: [...existingComments, newComment],
          };

          if (typeof updated.comments_count === 'number') {
            updated.comments_count += 1;
          } else if (typeof updated.comment_count === 'number') {
            updated.comment_count += 1;
          } else {
            const commentsLength = Array.isArray(updated.comments)
              ? updated.comments.length
              : 0;
            updated.comments_count = commentsLength;
            updated.comment_count = commentsLength;
          }

          return updated;
        },
      );
    },
    onError: (error) => {
      logError('Error creating comment', error as Error);
    },
  });

  const getSocialSentimentQuery = useQuery({
    enabled: hasParams && hasToken && Boolean(socialSentimentId),
    queryKey: ['social-sentiments', socialSentimentId],
    queryFn: () => getSocialSentiment(socialSentimentId as number, token),
    onError: (error) => {
      logError('Error fetching social sentiment', error as Error);
    },
  });

  const getSocialSentimentsQuery = useQuery({
    enabled: hasParams && hasToken,
    queryKey: ['social-sentiments', year, make, model, trim],
    retry: false,
    queryFn: ({ signal }) =>
      getSocialSentiments(
        { year, make, model, trim },
        token,
        undefined,
        undefined,
        signal,
      ),
    refetchOnMount: true,
    onError: (error) => {
      logError('Error fetching social sentiments', error as Error);
    },
  });

  return {
    getSocialSentimentQuery,
    getSocialSentimentsQuery,
    createCommentMutation,
  };
};
